import * as RadixPopover from '@radix-ui/react-popover';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DropdownSubProps } from './Dropdown.types';
import { DropdownSubContext, type DropdownSubContextValue } from './DropdownContext';

/**
 * Submenu root — **web** implementation.
 *
 * Nests a second Radix `Popover.Root` inside the parent menu:
 * `<DropdownSubTrigger>` anchors it, `<DropdownSubContent>` portals the
 * side-flyout. Opens on hover (with a short grace delay on leave so the
 * pointer can travel into the flyout) or on press (touch / keyboard).
 *
 * Unmounts with the parent `DropdownContent`, so closing the root menu
 * always resets the submenu.
 */

/** Grace delay (ms) before a hover-out actually closes the flyout. */
const SUB_CLOSE_DELAY_MS = 150;

export function DropdownSub({ children }: DropdownSubProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), SUB_CLOSE_DELAY_MS);
  }, [cancelClose]);

  const toggle = useCallback(() => {
    cancelClose();
    setOpen((prev) => !prev);
  }, [cancelClose]);

  // Don't fire a stale close after unmount (root menu closed mid-delay).
  useEffect(() => cancelClose, [cancelClose]);

  const ctx = useMemo<DropdownSubContextValue>(
    () => ({ open, toggle, openNow, scheduleClose, cancelClose }),
    [open, toggle, openNow, scheduleClose, cancelClose],
  );

  return (
    <DropdownSubContext.Provider value={ctx}>
      <RadixPopover.Root open={open} onOpenChange={setOpen} modal={false}>
        {children}
      </RadixPopover.Root>
    </DropdownSubContext.Provider>
  );
}
